import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Expense } from '@/types/expense';
import { format, parseISO } from 'date-fns';
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";

interface ExpenseDateChartProps {
  expenses: Expense[];
}

export const ExpenseDateChart = ({ expenses }: ExpenseDateChartProps) => {
  const dailyExpenses = expenses.reduce((acc, expense) => {
    const date = format(parseISO(expense.date), 'yyyy-MM-dd');
    acc[date] = (acc[date] || 0) + Number(expense.amount);
    return acc;
  }, {} as Record<string, number>);

  const data = Object.entries(dailyExpenses).map(([date, amount]) => ({
    date,
    amount,
  })).sort((a, b) => a.date.localeCompare(b.date));

  if (expenses.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[300px] text-center">
        <p className="text-muted-foreground">Add expenses to see daily spending trends</p>
      </div>
    );
  }

  return (
    <ScrollArea className="w-full h-full">
      <div className="min-w-[300px] md:min-w-[600px] h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart 
            data={data} 
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            className="bg-gradient-to-br from-white/95 to-white/90 backdrop-blur-sm rounded-lg shadow-lg"
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tickFormatter={(date) => format(parseISO(date), 'MMM dd')}
            />
            <YAxis unit="₹" />
            <Tooltip
              labelFormatter={(date) => format(parseISO(date as string), 'MMMM dd, yyyy')}
              formatter={(value: number) => [`₹${value.toFixed(2)}`, 'Amount']}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            />
            <Line 
              type="monotone" 
              dataKey="amount" 
              stroke="#8884d8" 
              activeDot={{ r: 8 }} 
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
};